import { useLazyQuery, useMutation } from "@apollo/client";
import {
  BellAlertIcon,
  BellSlashIcon,
  ExclamationTriangleIcon,
  GifIcon,
  GlobeAltIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  StarIcon,
  UserCircleIcon,
  UserIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { PhotoCamera } from "@mui/icons-material";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import PhotoHashBadge from '../../components/PhotoHashBadge/PhotoHashBadge';
import type { UserWithPhotoHash } from '../../graphql/photoHash';
import PlatformIcon from "../../components/PlatformIcon/PlatformIcon";
import Badge, { BadgeTone } from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaActionLabel,
  MediaActionRow,
  MediaActions,
  MediaBadges,
  MediaCard,
  MediaFooter,
  MediaFooterRow,
  MediaFrame,
  MediaGrid,
  MediaIconChip,
  MediaImage,
  MediaMeta,
  MediaTitle,
  MediaTop,
} from "../../components/Styled/Media";
import { Gender, PhotoRejectReason, User } from "../../graphql/graphql";
import { APPROVE_PROFILE_PHOTO, FIX_MAN } from "../../graphql/mutations-gql";
import { PROFILE_PHOTOS_APPROVAL } from "../../graphql/queries-gql";

// Fixed gender colors: they sit on photos and must read the same in both themes.
const GENDER_STYLE: Record<string, { label: string; color: string }> = {
  [Gender.Man]: { label: "♂ Man", color: "#3b82f6" },
  [Gender.NonBinary]: { label: "⚧ Non-binary", color: "#8b5cf6" },
  [Gender.Woman]: { label: "♀ Woman", color: "#ec4899" },
};

const genderStyle = (gender: Gender) =>
  GENDER_STYLE[gender] ?? GENDER_STYLE[Gender.Woman];

const REJECT_ACTIONS: Array<{
  reason: PhotoRejectReason;
  label: string;
  icon: JSX.Element;
}> = [
  {
    reason: PhotoRejectReason.ImageFoundOnInternet,
    label: "Reject: found on internet",
    icon: <GlobeAltIcon />,
  },
  {
    reason: PhotoRejectReason.NotAPerson,
    label: "Reject: not a person",
    icon: <UserIcon />,
  },
  {
    reason: PhotoRejectReason.PoorQuality,
    label: "Reject: poor quality",
    icon: <PhotoCamera />,
  },
  {
    reason: PhotoRejectReason.ContainsGraphics,
    label: "Reject: contains graphics",
    icon: <GifIcon />,
  },
  {
    reason: PhotoRejectReason.Inappropriate,
    label: "Reject: inappropriate",
    icon: <HandThumbDownIcon />,
  },
  {
    reason: PhotoRejectReason.ContainsChildren,
    label: "Reject: contains children",
    icon: <ShieldExclamationIcon />,
  },
  {
    reason: PhotoRejectReason.GenderMismatch,
    label: "Reject: gender mismatch",
    icon: <ScaleIcon />,
  },
  {
    reason: PhotoRejectReason.MultiplePeople,
    label: "Reject: multiple people",
    icon: <UsersIcon />,
  },
];

// Big and loud so gender reads at a glance when tapping through the queue.
const GenderBadge = styled(Badge)<{ $color: string }>`
  padding: 5px 11px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: ${(props) => props.$color};
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.85),
    0 2px 8px rgba(0, 0, 0, 0.4);
`;

// Women get a thick frame, a glow and a tinted footer so they stand out when
// tapping fast. The frame is an overlay, not a border, so every card keeps the
// same size and the hover buttons stay in the same spot from card to card.
const GenderCard = styled(MediaCard)<{ $color: string; $loud?: boolean }>`
  position: relative;

  ${(props) =>
    props.$loud
      ? `
    box-shadow: 0 0 16px 2px ${props.$color}99;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 5;
      border: 7px solid ${props.$color};
      border-radius: inherit;
      pointer-events: none;
    }

    ${MediaFooter} {
      background: ${props.$color}59;
    }
  `
      : ""}
`;

// Flags can be long (celebrity names, label lists), so let them wrap.
const FlagBadge = styled(Badge)`
  max-width: 100%;
  padding: 4px 8px;
  font-size: 12.5px;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
`;

// Fixed gold: premium users are worth a second look, so the whole card glows.
const PREMIUM_GOLD = "#f5b301";

const PremiumBadge = styled(Badge)`
  padding: 4px 9px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: linear-gradient(135deg, #f5b301 0%, #f08c00 100%);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.55),
    0 2px 10px rgba(245, 179, 1, 0.65);

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Gold ring sits outside the gender border with a gap, so both stay visible.
const PremiumCard = styled(GenderCard)`
  outline: 3px solid ${PREMIUM_GOLD};
  outline-offset: 3px;
  box-shadow: 0 0 22px 6px rgba(245, 179, 1, 0.45);
`;

const UserLink = styled(Link)`
  flex: 1;
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PushIcon = styled.span<{ $on: boolean }>`
  display: inline-flex;
  color: ${(props) =>
    props.$on ? props.theme.colors.green : props.theme.colors.red};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const RecentStrip = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
`;

const RecentItem = styled.button<{ $color: string }>`
  position: relative;
  flex-shrink: 0;
  width: 84px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: center;
  font-family: inherit;

  img {
    display: block;
    width: 84px;
    height: 134px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid ${(props) => props.$color};
  }
`;

const RecentOverlay = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 84px;
  height: 134px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${RecentItem}:hover &,
  ${RecentItem}:focus-visible & {
    opacity: 1;
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;

const RecentName = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11.5px;
  color: ${(props) => props.theme.secondaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

enum DateRange {
  Today,
  Yesterday,
  ThisMonth,
  Last24Hours,
  Last3Days,
  Last7Days,
  Last30Days,
  All,
}

const ProfilePhotos = () => {
  const [fromDate, setFromDate] = useState<Date>(addHours(new Date(), -24));
  const [toDate, setToDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Last24Hours);

  const [fetchPhotos, { data, loading, error }] = useLazyQuery(
    PROFILE_PHOTOS_APPROVAL,
    {
      fetchPolicy: "network-only",
      variables: {
        args: {
          fromDate,
          toDate,
        },
      },
      onCompleted: (data) => {
        setUsers(data.profilePhotosApproval || []);
      },
    },
  );

  useEffect(() => {
    fetchPhotos();
  }, [fromDate, toDate, fetchPhotos]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    switch (range) {
      case DateRange.All:
        setFromDate(new Date(0));
        setToDate(new Date());
        break;
      case DateRange.Today:
        setFromDate(startOfDay(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Yesterday:
        setFromDate(startOfDay(addDays(new Date(), -1)));
        setToDate(endOfDay(addDays(new Date(), -1)));
        break;
      case DateRange.ThisMonth:
        setFromDate(startOfMonth(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Last24Hours:
        setFromDate(addHours(new Date(), -24));
        setToDate(new Date());
        break;
      case DateRange.Last3Days:
        setFromDate(addDays(new Date(), -3));
        setToDate(new Date());
        break;
      case DateRange.Last7Days:
        setFromDate(addDays(new Date(), -7));
        setToDate(new Date());
        break;
      case DateRange.Last30Days:
        setFromDate(addDays(new Date(), -30));
        setToDate(new Date());
        break;
    }
  };

  const [approveProfilePhoto] = useMutation(APPROVE_PROFILE_PHOTO);
  const [users, setUsers] = useState<UserWithPhotoHash[]>([]);
  const [recentlyApproved, setRecentlyApproved] = useState<UserWithPhotoHash[]>([]);
  const [fixMan] = useMutation(FIX_MAN);

  const MAX_RECENTLY_APPROVED = 10;

  const handleApprove = (
    value: boolean,
    userId: string,
    rejectReason: PhotoRejectReason | null | undefined,
  ) => {
    const approvedUser = users.find((user) => user._id === userId);

    approveProfilePhoto({
      variables: {
        userId,
        value,
        rejectReason,
      },
      onCompleted: (data) => {
        console.log("approveProfilePhoto onCompleted", data);
      },
      onError: (error) => {
        console.log("approveProfilePhoto onError", error);
      },
    });

    // Lets immediately remove the user from the list so it does not feel laggy
    setUsers((prev) => {
      return prev.filter((user) => user._id !== userId);
    });

    // Track approved photos so the user can undo mistakes
    if (value && approvedUser) {
      setRecentlyApproved((prev) => {
        return [approvedUser, ...prev].slice(0, MAX_RECENTLY_APPROVED);
      });
    }
  };

  const handleUnapproveRecent = (userId: string) => {
    const unapprovedUser = recentlyApproved.find((user) => user._id === userId);

    setRecentlyApproved((prev) => prev.filter((user) => user._id !== userId));

    // Move back to the approval queue for re-review (no API call since
    // approveProfilePhoto(value: false) would reject and delete the photo)
    if (unapprovedUser) {
      setUsers((prev) => [unapprovedUser, ...prev]);
    }
  };

  const handleFixMan = (userId: string) => {
    fixMan({
      variables: {
        userId,
      },
      onCompleted: (data) => {
        console.log("fixMan onCompleted");
      },
      onError: (error) => {
        console.log("fixMan onError", error);
      },
      refetchQueries: [PROFILE_PHOTOS_APPROVAL],
    });
  };

  const showImage = (src: string) => {
    window.open(src, "_blank");
  };

  // What Rekognition flagged (celebrity, graphics, unsafe content, review
  // reasons). These are shown as colored badges on the photo so they stand out.
  const moderationFlags = (
    user: User,
  ): Array<{ key: string; label: string; tone: BadgeTone }> => {
    const m = user.profilePhoto?.moderation;
    if (!m) return [];
    const flags: Array<{ key: string; label: string; tone: BadgeTone }> = [];
    if (m.celebrityName) {
      flags.push({
        key: "celebrity",
        label: `Celebrity: ${m.celebrityName}${m.celebrityConfidence != null ? ` ${m.celebrityConfidence}%` : ""}`,
        tone: "red",
      });
    }
    if (m.moderationLabels?.length) {
      flags.push({
        key: "moderation",
        label: m.moderationLabels.join(", "),
        tone: "red",
      });
    }
    if (m.graphicsLabels?.length) {
      flags.push({
        key: "graphics",
        label: `Graphics: ${m.graphicsLabels.join(", ")}`,
        tone: "orange",
      });
    }
    if (m.reviewReasons?.length) {
      flags.push({
        key: "review",
        label: `Review: ${m.reviewReasons.join(", ")}`,
        tone: "orange",
      });
    }
    return flags;
  };

  // Informational only (detected gender), kept as a quiet meta line.
  const moderationDetails = (user: User): string | null => {
    const m = user.profilePhoto?.moderation;
    if (!m?.genderDetected) return null;
    return `Detected ${m.genderDetected}${m.genderConfidence != null ? ` ${m.genderConfidence}%` : ""}`;
  };

  const openGoogleLens = (imageUrl: string) => {
    const encoded = encodeURIComponent(imageUrl);
    const url = `https://lens.google.com/uploadbyurl?url=${encoded}`;
    const w = 800;
    const h = 1200;
    const left = (window.screen.width - w) / 2;
    const top = (window.screen.height - h) / 2;
    window.open(
      url,
      "google-lens",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
  };

  // Estimated age drives the badge color: under 18 is the one to catch.
  const ageTone = (ageRangeLow: number): BadgeTone => {
    if (ageRangeLow < 18) return "red";
    if (ageRangeLow < 21) return "orange";
    return "green";
  };

  // Even the top of the estimated range is a teenager: worth a hard look.
  const YOUNG_MAX_AGE = 19;
  const isYoungRange = (user: User): boolean => {
    const high = user.profilePhoto?.moderation?.ageRangeHigh;
    return high != null && high <= YOUNG_MAX_AGE;
  };

  return (
    <Page
      title="Profile photos"
      description="Hover a photo to approve it or reject it with a reason. Click it to open the full image."
      badge={
        users.length > 0 && (
          <Badge $tone="primary">{users.length} in queue</Badge>
        )
      }
      actions={
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, value) =>
            value !== null && handleDateRangeChange(value)
          }
          size="small"
        >
          <ToggleButton value={DateRange.Today}>Today</ToggleButton>
          <ToggleButton value={DateRange.Yesterday}>Yesterday</ToggleButton>
          <ToggleButton value={DateRange.ThisMonth}>This month</ToggleButton>
          <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
          <ToggleButton value={DateRange.Last3Days}>3 days</ToggleButton>
          <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
          <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
          <ToggleButton value={DateRange.All}>All</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {loading && (
        <Row>
          <Loader />
        </Row>
      )}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && users.length === 0 && (
        <EmptyState
          Icon={UserCircleIcon}
          title="Queue is empty"
          description="No profile photos waiting for review in this period."
        />
      )}

      <MediaGrid $min={270}>
        {users.map((user) => {
          const gender = genderStyle(user.gender);
          const isWoman = user.gender === Gender.Woman;
          const moderation = user.profilePhoto?.moderation;
          const details = moderationDetails(user);
          const flags = moderationFlags(user);
          const photoUrl = user.profilePhoto?.url ?? "";
          const Card = user.premium ? PremiumCard : GenderCard;
          const young = isYoungRange(user);

          return (
            <Card
              key={user.profilePhoto?.uploadId}
              $color={gender.color}
              $loud={isWoman}
              $accent={gender.color}
            >
              <MediaFrame onClick={() => showImage(photoUrl)}>
                <MediaImage src={photoUrl} alt="" loading="lazy" />
                <MediaTop>
                  <MediaBadges>
                    <GenderBadge $solid $color={gender.color}>
                      {gender.label}
                    </GenderBadge>
                    {user.profilePhotoAutoApprovedNeedReview ? (
                      <Badge $solid $tone="orange">
                        Auto-approved
                      </Badge>
                    ) : (
                      <Badge $solid $tone="red">
                        Failed auto-approval
                      </Badge>
                    )}
                    {young && (
                      <FlagBadge $solid $tone="red">
                        <ExclamationTriangleIcon />
                        Max age {moderation?.ageRangeHigh}
                      </FlagBadge>
                    )}
                    {flags.map((flag) => (
                      <FlagBadge key={flag.key} $solid $tone={flag.tone}>
                        <ExclamationTriangleIcon />
                        {flag.label}
                      </FlagBadge>
                    ))}
                  </MediaBadges>
                  <MediaBadges $align="end">
                    <PhotoHashBadge hasHash={user.profilePhoto?.hasPhotoHash} />
                    <MediaIconChip title={user.platform ?? undefined}>
                      <PlatformIcon platform={user.platform} />
                    </MediaIconChip>
                    {user.premium && (
                      <PremiumBadge $solid>
                        <StarIcon />
                        Premium
                      </PremiumBadge>
                    )}
                  </MediaBadges>
                </MediaTop>

                <MediaActions onClick={(e) => e.stopPropagation()}>
                  <MediaActionRow>
                    <ActionButton
                      label="Approve"
                      tone="approve"
                      size={46}
                      onClick={() => handleApprove(true, user._id, undefined)}
                    >
                      <HandThumbUpIcon />
                    </ActionButton>
                    <ActionButton
                      label="Fix gender: set to man"
                      tone="warn"
                      onClick={() => handleFixMan(user._id)}
                    >
                      <ScaleIcon />
                    </ActionButton>
                    <ActionButton
                      label="Search with Google Lens"
                      onClick={() => openGoogleLens(photoUrl)}
                    >
                      <MagnifyingGlassIcon />
                    </ActionButton>
                  </MediaActionRow>

                  <MediaActionLabel>Reject as</MediaActionLabel>
                  <MediaActionRow>
                    {REJECT_ACTIONS.map((action) => (
                      <ActionButton
                        key={action.reason}
                        label={action.label}
                        tone="reject"
                        size={34}
                        onClick={() =>
                          handleApprove(false, user._id, action.reason)
                        }
                      >
                        {action.icon}
                      </ActionButton>
                    ))}
                  </MediaActionRow>
                </MediaActions>
              </MediaFrame>

              <MediaFooter>
                <MediaFooterRow>
                  <UserLink to={`/user/${user._id}`} title="Open user">
                    <MediaTitle as="span" style={{ display: "block" }}>
                      {user.username}
                    </MediaTitle>
                  </UserLink>
                  {moderation?.ageRangeLow != null &&
                    moderation?.ageRangeHigh != null && (
                      <Tooltip title="Estimated age range" placement="top">
                        <Badge
                          $solid={young}
                          $tone={young ? "red" : ageTone(moderation.ageRangeLow)}
                        >
                          Age {moderation.ageRangeLow}-{moderation.ageRangeHigh}
                        </Badge>
                      </Tooltip>
                    )}
                  <Tooltip
                    title={
                      user.pushAvailable ? "Push enabled" : "Push not available"
                    }
                    placement="top"
                  >
                    <PushIcon $on={!!user.pushAvailable}>
                      {user.pushAvailable ? <BellAlertIcon /> : <BellSlashIcon />}
                    </PushIcon>
                  </Tooltip>
                </MediaFooterRow>
                <MediaMeta>
                  {[
                    user.country,
                    user.storeFront ? `Store ${user.storeFront}` : null,
                    [user.platform, user.appVersion].filter(Boolean).join(" "),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </MediaMeta>
                {details && <MediaMeta>{details}</MediaMeta>}
              </MediaFooter>
            </Card>
          );
        })}
      </MediaGrid>

      {recentlyApproved.length > 0 && (
        <Panel
          title="Recently approved"
          description="Click a photo to send it back to the queue."
        >
          <RecentStrip>
            {recentlyApproved.map((user) => (
              <RecentItem
                key={user._id}
                $color={genderStyle(user.gender).color}
                onClick={() => handleUnapproveRecent(user._id)}
                title="Move back to the queue"
              >
                <img
                  src={user.profilePhoto?.url ?? ""}
                  alt={user.username ?? ""}
                />
                <RecentOverlay>
                  <XMarkIcon />
                </RecentOverlay>
                <RecentName>{user.username}</RecentName>
              </RecentItem>
            ))}
          </RecentStrip>
        </Panel>
      )}
    </Page>
  );
};
export default ProfilePhotos;
